// Performance optimization utilities for Core Web Vitals
import { useState, useEffect } from 'react';

// Custom hook for lazy loading images
export const useLazyLoadImage = (imageUrl, placeholder = '/api/placeholder/400/300') => {
  const [imageSrc, setImageSrc] = useState(placeholder);
  const [imageRef, setImageRef] = useState();

  useEffect(() => {
    let observer;
    
    if (imageRef && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              setImageSrc(imageUrl);
              observer.unobserve(imageRef);
            }
          });
        },
        { threshold: 0.1 }
      );
      observer.observe(imageRef);
    } else if (imageRef) {
      // Fallback for browsers without IntersectionObserver
      setImageSrc(imageUrl);
    }

    return () => {
      if (observer && imageRef) {
        observer.unobserve(imageRef);
      }
    };
  }, [imageRef, imageUrl]);

  return [imageSrc, setImageRef];
};

// Preload critical resources
export const preloadResource = (href, as = 'style', crossorigin = null) => {
  if (typeof window !== 'undefined') {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = href;
    link.as = as;
    if (crossorigin) link.crossorigin = crossorigin;
    document.head.appendChild(link);
  }
};

// Critical resource hints for better performance
export const addResourceHints = () => {
  if (typeof window !== 'undefined') {
    // Preconnect to external domains
    const preconnectDomains = [
      'https://fonts.googleapis.com',
      'https://fonts.gstatic.com',
      'https://www.googletagmanager.com'
    ];

    preconnectDomains.forEach(domain => {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = domain;
      link.crossorigin = 'anonymous';
      document.head.appendChild(link);
    });

    // DNS prefetch for likely user destinations
    const dnsPrefetchDomains = [
      'https://js.stripe.com'
    ];

    dnsPrefetchDomains.forEach(domain => {
      const link = document.createElement('link');
      link.rel = 'dns-prefetch';
      link.href = domain;
      document.head.appendChild(link);
    });
  }
};

// Debounce utility for performance-critical operations
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Throttle utility for scroll/resize events
export const throttle = (func, limit) => {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

// Check if device prefers reduced motion for animations
export const prefersReducedMotion = () => {
  if (typeof window !== 'undefined') {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  return false;
};

// Measure and report Core Web Vitals
export const measureCoreWebVitals = () => {
  if (typeof window !== 'undefined') {
    // Cumulative Layout Shift (CLS)
    let clsValue = 0;
    let clsEntries = [];

    const clsObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (!entry.hadRecentInput) {
          clsEntries.push(entry);
          clsValue += entry.value;
        }
      }
    });

    try {
      clsObserver.observe({ type: 'layout-shift', buffered: true });
    } catch (e) {
      // Fallback for older browsers
    }

    // Largest Contentful Paint (LCP)
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      
      // Report to analytics if available
      if (window.gtag) {
        window.gtag('event', 'web_vitals', {
          metric_name: 'LCP',
          metric_value: Math.round(lastEntry.startTime),
          metric_id: 'lcp'
        });
      }
    });

    try {
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
    } catch (e) {
      // Fallback for older browsers
    }

    // First Input Delay (FID)
    const fidObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        // Report to analytics if available
        if (window.gtag) {
          window.gtag('event', 'web_vitals', {
            metric_name: 'FID',
            metric_value: Math.round(entry.processingStart - entry.startTime),
            metric_id: 'fid'
          });
        }
      }
    });

    try {
      fidObserver.observe({ type: 'first-input', buffered: true });
    } catch (e) {
      // Fallback for older browsers
    }

    // Report CLS when the page lifecycle changes
    const reportCLS = () => {
      if (window.gtag && clsValue) {
        window.gtag('event', 'web_vitals', {
          metric_name: 'CLS',
          metric_value: Math.round(clsValue * 1000) / 1000,
          metric_id: 'cls'
        });
      }
    };

    // Listen for page lifecycle changes
    ['pagehide', 'beforeunload'].forEach(eventType => {
      window.addEventListener(eventType, reportCLS, { capture: true, once: true });
    });
  }
};

// Image optimization helpers
export const getOptimizedImageUrl = (originalUrl, width = 800, quality = 80) => {
  // If using a CDN or image service, add optimization parameters
  // For placeholder API, return as-is or with dimension parameters
  if (originalUrl.includes('/api/placeholder/')) {
    return originalUrl;
  }
  
  // Add your image optimization logic here
  return originalUrl;
};

// Efficient scroll position tracking
export const useScrollPosition = () => {
  const [scrollPosition, setScrollPosition] = useState(0);

  useEffect(() => {
    const updatePosition = throttle(() => {
      setScrollPosition(window.pageYOffset);
    }, 50);

    window.addEventListener('scroll', updatePosition);
    updatePosition();

    return () => window.removeEventListener('scroll', updatePosition);
  }, []);

  return scrollPosition;
};

// Bundle size analysis helper (for development)
export const analyzeBundleSize = () => {
  if (process.env.NODE_ENV === 'development') {
    // Log bundle information for optimization
    const scripts = document.querySelectorAll('script[src]');
    const stylesheets = document.querySelectorAll('link[rel="stylesheet"]');
    
    console.group('Bundle Analysis');
    console.log(`Scripts loaded: ${scripts.length}`);
    console.log(`Stylesheets loaded: ${stylesheets.length}`);
    
    scripts.forEach((script, index) => {
      console.log(`Script ${index + 1}: ${script.src}`);
    });
    
    stylesheets.forEach((style, index) => {
      console.log(`Stylesheet ${index + 1}: ${style.href}`);
    });
    console.groupEnd();
  }
};

export default {
  useLazyLoadImage,
  preloadResource,
  addResourceHints,
  debounce,
  throttle,
  prefersReducedMotion,
  measureCoreWebVitals,
  getOptimizedImageUrl,
  useScrollPosition,
  analyzeBundleSize
};